import React, { memo, useMemo } from 'react'
import PropTypes from 'prop-types'
import MuiAutocomplete from '@mui/material/Autocomplete'
import { Typography, Divider } from '@mui/material'
import clsx from 'clsx'
import ListBoxComponent from '../ListBoxComponent'
import TextField from '../TextField'
import useStyles from './Autocomplete.styled'

const Autocomplete = ({
	options = [],
	onChange,
	onFocus,
	onBlur,
	className,
	value,
	layout,
	localeTextDirection,
	InputProps = {},
	InputLabelProps = {},
	...rest
}) => {
	const { classes: classesProp, ...TextFieldProps } = rest
	const classes = useStyles({ ...rest, localeTextDirection })

	const curValue = useMemo(() => {
		return options.find(({ id }) => id === value) || null
	}, [options, value])

	const sortedOptions = useMemo(() => {
		return options.sort(({ is_custom: a }, { is_custom: b }) => {
			if (a < b) return 1
			return a > b ? -1 : 0
		})
	}, [options])

	return (
		<MuiAutocomplete
			disableListWrap
			disableClearable
			classes={{
				root: clsx(className, classes[layout], classes.root, InputProps.readOnly && classes.readonly),
				inputRoot: clsx(InputProps.readOnly && classes.readonly2, classes.inputRoot),
				input: classes.input,
				focused: classes.focused,
				listbox: classes.listBox,
				popupIndicator: classes.popupIndicator,
			}}
			ListboxComponent={ListBoxComponent}
			options={sortedOptions}
			onChange={onChange}
			onBlur={onBlur}
			onFocus={onFocus}
			value={curValue}
			groupBy={({ is_custom: custom }) => custom}
			renderGroup={({ key, group, children }) =>
				group
					? [
							...children,
							<Divider className={classes.divider} key={`divider-${key}`} />,
							// eslint-disable-next-line no-mixed-spaces-and-tabs
					  ]
					: children
			}
			getOptionLabel={({ option }) => option}
			renderInput={params => (
				<TextField
					layout={layout}
					{...TextFieldProps}
					{...params}
					InputProps={{ ...InputProps, ...params.InputProps }}
					inputProps={{ ...params.inputProps, autoComplete: 'none', name: null, type: 'text' }}
					InputLabelProps={{
						...InputLabelProps,
						...params.InputLabelProps,
					}}
				/>
			)}
			renderOption={renderProps => (
				<Typography {...renderProps} noWrap variant="caption">
					{renderProps.key}
				</Typography>
			)}
		/>
	)
}

Autocomplete.propTypes = {
	options: PropTypes.arrayOf(
		PropTypes.shape({
			option: PropTypes.string,
		}),
	).isRequired,
	className: PropTypes.string,
	classes: PropTypes.shape({
		listBox: PropTypes.string,
		field: PropTypes.string,
	}),
	InputProps: PropTypes.object,
	InputLabelProps: PropTypes.object,
	onChange: PropTypes.func,
	onFocus: PropTypes.func,
	onBlur: PropTypes.func,
	value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
	layout: PropTypes.string,
	localeTextDirection: PropTypes.string,
}

export default memo(Autocomplete)
